import { createBrowserRouter } from 'react-router';

import { DOCUMENT_TITLE } from '@/constants/system';

import { RouteErrorScreen } from 'components/system/RouteErrorScreen';

import { AppRoute } from 'containers/AppRoute';
import { RootRoute } from 'containers/RootRoute';

import { toLazyPage } from './assets';

const router = createBrowserRouter([
  {
    Component: RootRoute,
    ErrorBoundary: RouteErrorScreen,
    children: [
      {
        path: '/login',
        handle: { title: DOCUMENT_TITLE.login },
        lazy: toLazyPage(() => import('pages/LoginPage')),
      },
      {
        path: '/new-password',
        handle: { title: DOCUMENT_TITLE.newPassword },
        lazy: toLazyPage(() => import('pages/NewPasswordPage')),
      },
      {
        path: '/register',
        handle: { title: DOCUMENT_TITLE.register },
        lazy: toLazyPage(() => import('pages/RegisterPage')),
      },
      {
        path: '/two-factor',
        handle: { title: DOCUMENT_TITLE.twoFactor },
        lazy: toLazyPage(() => import('pages/TwoFactorPage')),
      },
      {
        path: '/two-factor/setup',
        handle: { title: DOCUMENT_TITLE.twoFactorSetup },
        lazy: toLazyPage(() => import('pages/TwoFactorSetupPage')),
      },
      {
        Component: AppRoute,
        children: [
          {
            index: true,
            lazy: toLazyPage(() => import('pages/HomePage')),
          },
          {
            path: '/activation-ready',
            lazy: toLazyPage(() => import('pages/ActivationReadyPage')),
          },
          {
            path: '/admin-manage',
            lazy: toLazyPage(() => import('pages/AdminManagePage')),
          },
          {
            path: '/all-applicants',
            lazy: toLazyPage(() => import('pages/AllApplicantsPage')),
          },
          {
            path: '/all-clients',
            lazy: toLazyPage(() => import('pages/AllClientsPage')),
          },
          {
            path: '/all-interviews',
            lazy: toLazyPage(() => import('pages/AllInterviewsListPage')),
          },
          {
            path: '/all-singles',
            lazy: toLazyPage(() => import('pages/AllSinglesPage')),
          },
          {
            path: '/applicants/:applicantID',
            lazy: toLazyPage(() => import('pages/ApplicantPage')),
          },
          {
            path: '/approved',
            lazy: toLazyPage(() => import('pages/ApprovedPage')),
          },
          {
            path: '/audit-log',
            lazy: toLazyPage(() => import('pages/AuditLogPage')),
          },
          {
            path: '/background-check',
            lazy: toLazyPage(() => import('pages/BackgroundCheckPage')),
          },
          {
            path: '/boss-applicants',
            lazy: toLazyPage(() => import('pages/BossApplicantsPage')),
          },
          {
            path: '/billing',
            lazy: toLazyPage(() => import('pages/BillingPage')),
          },
          {
            path: '/billing/need-review',
            lazy: toLazyPage(() => import('pages/NeedReviewPage')),
          },
          {
            path: '/billing/overall',
            lazy: toLazyPage(() => import('pages/OverallHistoryPage')),
          },
          {
            path: '/capacity',
            lazy: toLazyPage(() => import('pages/CapacityPage')),
          },
          {
            path: '/concierges',
            lazy: toLazyPage(() => import('pages/ConciergesPage')),
          },
          {
            path: '/create-rec',
            lazy: toLazyPage(() => import('pages/CreateRecPage')),
          },
          {
            path: '/failed-background-check',
            lazy: toLazyPage(() => import('pages/FailedBackgroundCheckPage')),
          },
          {
            path: '/final-approval',
            lazy: toLazyPage(() => import('pages/FinalApprovalPage')),
          },
          {
            path: '/further-review',
            lazy: toLazyPage(() => import('pages/FurtherReviewPage')),
          },
          {
            path: '/interviews-calendar',
            lazy: toLazyPage(() => import('pages/InterviewsCalendarPage')),
          },
          {
            path: '/invitation-policy',
            lazy: toLazyPage(() => import('pages/InvitationPolicyPage')),
          },
          {
            path: '/invitations',
            lazy: toLazyPage(() => import('pages/InvitationsPage')),
          },
          {
            path: '/invitations/:inviteID',
            lazy: toLazyPage(() => import('pages/InvitationDetailPage')),
          },
          {
            path: '/members/clients',
            lazy: toLazyPage(() => import('pages/ClientsPage')),
          },
          {
            path: '/members/singles',
            lazy: toLazyPage(() => import('pages/SinglesPage')),
          },
          {
            path: '/members/:memberID',
            lazy: toLazyPage(() => import('pages/MemberRecordPage')),
          },
          {
            path: '/messages',
            lazy: toLazyPage(() => import('pages/MessagesPage')),
          },
          {
            path: '/my-applicants',
            lazy: toLazyPage(() => import('pages/MyApplicantsPage')),
          },
          {
            path: '/my-clients',
            lazy: toLazyPage(() => import('pages/MyClientsPage')),
          },
          {
            path: '/my-interviews',
            lazy: toLazyPage(() => import('pages/MyInterviewsListPage')),
          },
          {
            path: '/my-interviews-calendar',
            lazy: toLazyPage(() => import('pages/MyInterviewsCalendarPage')),
          },
          {
            path: '/my-singles',
            lazy: toLazyPage(() => import('pages/MySinglesPage')),
          },
          {
            path: '/new-applicants',
            lazy: toLazyPage(() => import('pages/NewApplicantsPoolPage')),
          },
          {
            path: '/notifications',
            lazy: toLazyPage(() => import('pages/NotificationsSettingsPage')),
          },
          {
            path: '/on-the-app',
            lazy: toLazyPage(() => import('pages/OnTheAppPage')),
          },
          {
            path: '/pipeline-overview',
            lazy: toLazyPage(() => import('pages/PipelineOverviewPage')),
          },
          {
            path: '/recs',
            lazy: toLazyPage(() => import('pages/MyRecsPage')),
          },
          {
            path: '/safety-reports',
            lazy: toLazyPage(() => import('pages/SafetyReportsPage')),
          },
          {
            path: '/settings',
            lazy: toLazyPage(() => import('pages/SettingsPage')),
          },
          {
            path: '/singles-search',
            lazy: toLazyPage(() => import('pages/SinglesSearchPage')),
          },
          {
            path: '/verified',
            lazy: toLazyPage(() => import('pages/VerifiedPage')),
          },
          {
            path: '/waitlist',
            lazy: toLazyPage(() => import('pages/WaitlistPage')),
          },
        ],
      },
      {
        path: '*',
        lazy: toLazyPage(() => import('pages/NotFoundPage')),
      },
    ],
  },
]);

export {
  router,
};
